<template>
  <div class="page-content">
    <div class="page-header">
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div>
          <h1 class="page-title">Migration Loyverse</h1>
          <p class="page-subtitle">Importez votre catalogue et vos clients depuis Loyverse</p>
        </div>
        <v-btn text color="primary" @click="helpDialog = true">
          <v-icon left>mdi-help-circle-outline</v-icon>
          Comment ça marche ?
        </v-btn>
      </div>
    </div>

    <!-- Guide d'utilisation -->
    <v-dialog v-model="helpDialog" max-width="720" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-help-circle-outline</v-icon>
          Comment migrer vos données Loyverse
          <v-spacer></v-spacer>
          <v-btn icon @click="helpDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4 loyverse-help">
          <p>
            Cette page recopie automatiquement vos données Loyverse dans votre caisse :
            <strong>catégories</strong>, <strong>produits</strong> (prix, coût, code-barres, stock)
            et <strong>clients</strong>. Elle <strong>lit</strong> seulement votre compte Loyverse —
            elle n'y modifie rien.
          </p>

          <h4 class="mt-4 mb-1">1. Obtenir votre jeton Loyverse</h4>
          <ol class="mb-0">
            <li>Connectez-vous au back-office Loyverse : <strong>r.loyverse.com</strong>.</li>
            <li>Allez dans <strong>Intégrations → Jetons d'accès</strong>.</li>
            <li>Cliquez <strong>« + Ajouter un jeton d'accès »</strong>, nommez-le, enregistrez.</li>
            <li><strong>Copiez</strong> le jeton affiché.</li>
          </ol>

          <h4 class="mt-4 mb-1">2. Connecter votre compte</h4>
          <p class="mb-0">
            Collez le jeton dans le champ, puis cliquez <strong>« Vérifier &amp; enregistrer »</strong>.
            Le nom de votre commerce et votre devise s'affichent. Si le message
            « Jeton Loyverse invalide » apparaît, recopiez le jeton (étape 1).
          </p>

          <h4 class="mt-4 mb-1">3. Analyser</h4>
          <p class="mb-0">
            Cliquez <strong>« Analyser »</strong> pour voir combien de catégories, produits et clients
            seront importés. Cette étape ne modifie rien.
          </p>

          <h4 class="mt-4 mb-1">4. Importer</h4>
          <p class="mb-0">
            Cliquez <strong>« Importer »</strong> et laissez la page ouverte. À la fin, un résumé
            indique, pour chaque type, le nombre d'éléments <strong>créés</strong>,
            <strong>mis à jour</strong> et en <strong>erreur</strong>.
          </p>

          <v-alert dense text type="info" class="my-4">
            <div class="font-weight-medium mb-1">Bon à savoir</div>
            <ul class="mb-0">
              <li>Tout est importé en <strong>gourdes (HTG)</strong> — ajustez ensuite les prix qui étaient en dollars.</li>
              <li>Les <strong>variantes</strong> deviennent des produits séparés (ex. « T-shirt - Large »).</li>
              <li>Le <strong>stock</strong> est cumulé sur toutes les boutiques.</li>
              <li>Les articles sans catégorie sont rangés dans <strong>« Sans catégorie »</strong>.</li>
            </ul>
          </v-alert>

          <h4 class="mt-4 mb-1">Relancer sans doublon</h4>
          <p class="mb-0">
            Vous pouvez relancer l'import autant de fois que vous voulez : les éléments déjà présents
            sont <strong>mis à jour</strong>, pas dupliqués (produits reconnus par code-barres, clients
            par téléphone).
          </p>

          <h4 class="mt-4 mb-1">En cas d'erreurs</h4>
          <p class="mb-0">
            Si la colonne « Erreurs » n'est pas à zéro, téléchargez le <strong>journal d'erreurs</strong> :
            il liste les éléments non importés et la raison. Le reste est bien importé.
          </p>

          <h4 class="mt-4 mb-1">Se déconnecter</h4>
          <p class="mb-0">
            Le bouton <strong>« Déconnecter »</strong> retire le jeton de la caisse. Vos produits et
            clients déjà importés restent en place.
          </p>

          <h4 class="mt-4 mb-1">Importer l'historique des ventes</h4>
          <p class="mb-0">
            En bas de la page, section <strong>« Historique des ventes »</strong> : cliquez
            <strong>Analyser les ventes</strong> puis <strong>Importer les ventes</strong>. On reprend les
            ventes des derniers mois, en HTG, sans toucher au stock. <em>Importez d'abord le catalogue.</em>
          </p>

          <p class="text-caption mt-4 mb-0">
            Pas encore migrés : taxes détaillées, modificateurs, fournisseurs, employés.
          </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="helpDialog = false">Compris</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card flat class="table-card pa-4" :loading="loading">
      <!-- State A: not connected -->
      <div v-if="!connected">
        <h3 class="mb-2">Connecter votre compte Loyverse</h3>
        <ol class="mb-4">
          <li>Connectez-vous à Loyverse → <strong>Intégrations → Jetons d'accès</strong>.</li>
          <li>Cliquez <strong>« Ajouter un jeton d'accès »</strong> et copiez le jeton généré.</li>
          <li>Collez-le ci-dessous puis cliquez « Vérifier & enregistrer ».</li>
        </ol>
        <v-text-field
          v-model="token"
          label="Jeton d'accès Loyverse"
          outlined dense
          :error-messages="tokenError"
        ></v-text-field>
        <v-btn color="primary" :loading="saving" :disabled="!token" @click="saveToken">
          Vérifier &amp; enregistrer
        </v-btn>
      </div>

      <!-- State B/C/D: connected -->
      <div v-else>
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <h3 class="mb-1">{{ integration.business_name || 'Compte Loyverse' }}</h3>
            <p class="mb-0">
              Devise : {{ integration.currency_code || 'n/d' }} ·
              Connecté le {{ formatDate(integration.connected_at) }}
              <span v-if="integration.last_import_at"> · Dernier import : {{ formatDate(integration.last_import_at) }}</span>
            </p>
          </div>
          <v-btn text color="error" :disabled="busy" @click="disconnect">Déconnecter</v-btn>
        </div>

        <v-alert dense text type="info" class="mb-4">
          Tous les produits et clients seront importés en <strong>HTG</strong>. L'import est ré-exécutable
          sans créer de doublons.
        </v-alert>

        <div class="mb-4">
          <v-btn color="secondary" class="mr-2" :loading="run.type === 'preflight' && busy" :disabled="busy" @click="start('preflight')">
            Analyser
          </v-btn>
          <v-btn color="primary" :loading="run.type === 'import' && busy" :disabled="busy" @click="start('import')">
            Importer
          </v-btn>
        </div>

        <!-- Progress / counts -->
        <v-card outlined class="pa-3" v-if="run.status">
          <div class="mb-2">
            <strong>{{ run.type === 'preflight' ? 'Analyse' : 'Import' }}</strong> —
            statut : {{ statusLabel(run.status) }}
          </div>
          <v-progress-linear v-if="busy" indeterminate color="primary" class="mb-3"></v-progress-linear>

          <v-simple-table dense v-if="run.counts || run.results">
            <thead>
              <tr><th>Entité</th><th>Total</th><th>Créés</th><th>Mis à jour</th><th>Erreurs</th></tr>
            </thead>
            <tbody>
              <tr v-for="e in entities" :key="e.key">
                <td>{{ e.label }}</td>
                <td>{{ count(e.key) }}</td>
                <td>{{ result(e.key, 'created') }}</td>
                <td>{{ result(e.key, 'updated') }}</td>
                <td>{{ result(e.key, 'errors') }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div v-if="run.error_log && run.error_log.length" class="mt-3">
            <v-btn small text color="error" @click="downloadErrors">
              Télécharger le journal d'erreurs ({{ run.error_log.length }})
            </v-btn>
          </div>
        </v-card>

        <v-divider class="my-5"></v-divider>

        <h3 class="mb-1">Historique des ventes</h3>
        <v-alert dense text type="info" class="mb-3">
          Importez vos ventes Loyverse (<strong>{{ salesPeriodLabel }}</strong>), en HTG.
          N'affecte pas le stock. Importez d'abord le catalogue ci-dessus.
        </v-alert>
        <v-select
          v-model="salesDays"
          :items="salesPeriodOptions"
          label="Période à importer"
          outlined dense
          :disabled="salesBusy"
          style="max-width: 280px"
          class="mb-1"
        ></v-select>
        <p class="text-caption mb-4">
          Par défaut <strong>31 derniers jours</strong>. Au-delà (3, 6 ou 12 mois), Loyverse exige un
          <strong>compte payant</strong> pour l'accès à l'historique étendu ; sinon l'import se limite
          automatiquement aux 31 derniers jours.
        </p>
        <div class="mb-4">
          <v-btn color="secondary" class="mr-2" :loading="salesRun.type === 'preflight' && salesBusy" :disabled="salesBusy" @click="startSales('preflight')">
            Analyser les ventes
          </v-btn>
          <v-btn color="primary" :loading="salesRun.type === 'import' && salesBusy" :disabled="salesBusy" @click="startSales('import')">
            Importer les ventes
          </v-btn>
        </div>
        <v-card outlined class="pa-3" v-if="salesRun.status">
          <div class="mb-2">
            <strong>{{ salesRun.type === 'preflight' ? 'Analyse des ventes' : 'Import des ventes' }}</strong> —
            statut : {{ statusLabel(salesRun.status) }}
          </div>
          <v-progress-linear v-if="salesBusy" indeterminate color="primary" class="mb-3"></v-progress-linear>
          <div v-if="salesRun.counts">Tickets analysés : {{ salesRun.counts.receipts != null ? salesRun.counts.receipts : '—' }}</div>
          <v-simple-table dense v-if="salesRun.results && salesRun.results.sales">
            <tbody>
              <tr><td>Ventes créées</td><td>{{ salesRun.results.sales.created }}</td></tr>
              <tr><td>Mises à jour</td><td>{{ salesRun.results.sales.updated }}</td></tr>
              <tr><td>Retours</td><td>{{ salesRun.results.sales.refunds }}</td></tr>
              <tr><td>Erreurs</td><td>{{ salesRun.results.sales.errors }}</td></tr>
            </tbody>
          </v-simple-table>
          <v-alert v-if="salesRun.results && salesRun.results.note" dense text type="warning" class="mt-3 mb-0">
            {{ salesRun.results.note }}
          </v-alert>
          <div v-if="salesRun.error_log && salesRun.error_log.length" class="mt-3">
            <v-btn small text color="error" @click="downloadSalesErrors">
              Télécharger le journal d'erreurs ({{ salesRun.error_log.length }})
            </v-btn>
          </div>
        </v-card>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'LoyverseImportView',
  data: () => ({
    helpDialog: false,
    loading: true,
    saving: false,
    busy: false,
    connected: false,
    integration: {},
    token: '',
    tokenError: '',
    run: { id: null, type: null, status: null, counts: null, results: null, error_log: null },
    poll: null,
    salesDays: 31,
    salesPeriodOptions: [
      { text: '31 derniers jours (gratuit)', value: 31 },
      { text: '3 derniers mois (compte payant)', value: 90 },
      { text: '6 derniers mois (compte payant)', value: 180 },
      { text: '12 derniers mois (compte payant)', value: 365 }
    ],
    salesBusy: false,
    salesRun: { id: null, type: null, status: null, counts: null, results: null, error_log: null },
    salesPoll: null,
    entities: [
      { key: 'categories', label: 'Catégories' },
      { key: 'products', label: 'Produits' },
      { key: 'customers', label: 'Clients' }
    ]
  }),
  computed: {
    salesPeriodLabel() {
      const o = this.salesPeriodOptions.find(x => x.value === this.salesDays)
      return o ? o.text : this.salesDays + ' jours'
    }
  },
  methods: {
    formatDate(d) {
      return d ? new Date(d).toLocaleString('fr-FR') : '—'
    },
    statusLabel(s) {
      return { pending: 'En attente', running: 'En cours', completed: 'Terminé', failed: 'Échec' }[s] || s
    },
    count(key) {
      return (this.run.counts && this.run.counts[key] != null) ? this.run.counts[key] : '—'
    },
    result(key, field) {
      return (this.run.results && this.run.results[key]) ? this.run.results[key][field] : '—'
    },
    loadIntegration() {
      this.loading = true
      this.$axios.get('/loyverse/integration')
        .then(r => {
          this.connected = r.data.connected
          this.integration = r.data.integration || {}
        })
        .catch(() => {})
        .finally(() => { this.loading = false })
    },
    saveToken() {
      this.saving = true
      this.tokenError = ''
      this.$axios.post('/loyverse/integration', { api_token: this.token })
        .then(() => { this.token = ''; this.loadIntegration() })
        .catch(e => {
          this.tokenError = (e.response && e.response.data && e.response.data.message) || 'Jeton invalide'
        })
        .finally(() => { this.saving = false })
    },
    disconnect() {
      this.busy = true
      this.$axios.delete('/loyverse/integration')
        .then(() => { this.connected = false; this.integration = {}; this.resetRun() })
        .finally(() => { this.busy = false })
    },
    resetRun() {
      this.run = { id: null, type: null, status: null, counts: null, results: null, error_log: null }
    },
    start(mode) {
      this.busy = true
      this.resetRun()
      this.run.type = mode
      this.$axios.post('/loyverse/' + (mode === 'preflight' ? 'preflight' : 'import'))
        .then(r => { this.run.id = r.data.run_id; this.startPolling() })
        .catch(() => { this.busy = false })
    },
    startPolling() {
      this.clearPolling()
      this.poll = setInterval(this.checkStatus, 2000)
      this.checkStatus()
    },
    checkStatus() {
      if (!this.run.id) return
      this.$axios.get('/loyverse/import/' + this.run.id + '/status')
        .then(r => {
          this.run = Object.assign({}, this.run, r.data)
          if (r.data.status === 'completed' || r.data.status === 'failed') {
            this.clearPolling()
            this.busy = false
            if (r.data.type === 'import') this.loadIntegration()
          }
        })
        .catch(() => { this.clearPolling(); this.busy = false })
    },
    clearPolling() {
      if (this.poll) { clearInterval(this.poll); this.poll = null }
    },
    downloadErrors() {
      const blob = new Blob([(this.run.error_log || []).join('\n')], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'loyverse-erreurs.txt'
      a.click()
      URL.revokeObjectURL(url)
    },
    startSales(mode) {
      this.salesBusy = true
      this.salesRun = { id: null, type: mode, status: null, counts: null, results: null, error_log: null }
      this.$axios.post('/loyverse/sales/' + (mode === 'preflight' ? 'preflight' : 'import'), { days: this.salesDays })
        .then(r => { this.salesRun.id = r.data.run_id; this.startSalesPolling() })
        .catch(() => { this.salesBusy = false })
    },
    startSalesPolling() {
      this.clearSalesPolling()
      this.salesPoll = setInterval(this.checkSalesStatus, 2000)
      this.checkSalesStatus()
    },
    checkSalesStatus() {
      if (!this.salesRun.id) return
      this.$axios.get('/loyverse/import/' + this.salesRun.id + '/status')
        .then(r => {
          this.salesRun = Object.assign({}, this.salesRun, r.data)
          if (r.data.status === 'completed' || r.data.status === 'failed') {
            this.clearSalesPolling()
            this.salesBusy = false
          }
        })
        .catch(() => { this.clearSalesPolling(); this.salesBusy = false })
    },
    clearSalesPolling() {
      if (this.salesPoll) { clearInterval(this.salesPoll); this.salesPoll = null }
    },
    downloadSalesErrors() {
      const blob = new Blob([(this.salesRun.error_log || []).join('\n')], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'loyverse-ventes-erreurs.txt'
      a.click()
      URL.revokeObjectURL(url)
    }
  },
  mounted() { this.loadIntegration() },
  beforeDestroy() { this.clearPolling(); this.clearSalesPolling() }
}
</script>
